import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAtom, Provider } from 'jotai';
import { Button, Space } from 'antd';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import { EditWrapper, StyledMetricCaption, StyledMetricTitle } from './styled';
import { supportedMetricsSelector } from '@src/store/common/selectors';
import useEventGroupBy from './useEventGroupBy';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import SelectEvent from './SelectEvent';
import apptracker from '@src/utils/sensor/apptracker';
import { EVENT_METRIC_WIDGET_METRIC_CHANGE } from '@src/utils/sensor/track';
import { getLimitationByUsecaseKey } from '@hooks/useCurrentLimitation';
import { getValidPresetKeys } from '@src/utils/presets/utils';
import { currentKpiIdAtom } from '@src/atoms/panels';

const PRESET_KEYS = getValidPresetKeys();

function Edit({ wid, selectedMetrics, onSave, onCancel, selectedEventName }) {
  const [currentMetrics, setCurrentMetrics] = useState([]);
  const supportedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const [eventName, setEventName] = useState(selectedEventName);
  const { eventList = [], isLoading } = useEventGroupBy({ wid });
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const limitation = getLimitationByUsecaseKey(PRESET_KEYS.APP_EVENTS);
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);

  useEffect(() => {
    const filteredMetrics = selectedMetrics.filter((m) => m in supportedMetrics);
    setCurrentMetrics(filteredMetrics);
  }, [selectedMetrics, supportedMetrics]);

  const metrics = useSupportedMetrics({
    excludeCategories: globalExcludeMetricsCategory,
    limitation,
    allMetrics: supportedMetrics,
  });

  const onApply = (updatedMetrics) => {
    setCurrentMetrics(updatedMetrics);
    apptracker(EVENT_METRIC_WIDGET_METRIC_CHANGE, { metrics: updatedMetrics });
  };

  const handleSave = () => {
    onSave({ metrics: currentMetrics, eventName });
  };

  return (
    <EditWrapper>
      <StyledMetricTitle>
        <div>Select Event </div>
        <span>(Events are filtered based on the applied global filter)</span>
      </StyledMetricTitle>
      <Space direction="vertical">
        <SelectEvent
          eventList={eventList}
          onChange={setEventName}
          eventName={eventName}
          isLoading={isLoading}
        />
        <Space>
          <Provider>
            <MetricSelectorPopup
              supportedMetrics={metrics}
              selectedMetrics={currentMetrics}
              lockedMetrics={[]}
              RenderBtn={Button}
              onApply={onApply}
              metricsCountLimit={4}
              config={limitation?.metrics?.config}
              currentKpiId={currentKpiId}
              onKpiChange={setCurrentKpiId}
            />
          </Provider>
          <StyledMetricCaption>
            {currentMetrics.length > 1
              ? `${currentMetrics.length} selected`
              : 'Min 2 metrics required'}
          </StyledMetricCaption>
        </Space>
      </Space>
      <Space>
        <Button
          type="primary"
          size="small"
          onClick={handleSave}
          disabled={currentMetrics.length < 2 || !eventName}
        >
          Save
        </Button>
        <Button
          type="default"
          size="small"
          onClick={onCancel}
          disabled={selectedMetrics.length < 2 || !eventName}
        >
          Cancel
        </Button>
      </Space>
    </EditWrapper>
  );
}

export default Edit;
